<template>
  <div class="notification-detail">
    <div class="detail-container">
      <!-- Header with Severity Badge -->
      <div class="detail-header">
        <div class="severity-badge" :class="`severity-${notification.severity}`">
          {{ getSeverityLabel(notification.severity) }}
        </div>
        <span class="timestamp">{{ formatTimestamp(notification.created_at) }}</span>
      </div>

      <!-- Title -->
      <h1 class="notification-title">{{ notification.title }}</h1>

      <!-- EditorJS Content -->
      <div class="notification-content">
        <EditorJsRenderer :data="notification.content" :dark-mode="true" />
      </div>
    </div>

    <!-- Action Buttons (Sticky Footer) -->
    <div class="action-footer">
      <button
        v-if="!notification.is_read"
        @click="markAsRead"
        :disabled="processing"
        class="btn-action btn-mark-read"
      >
        <font-awesome-icon
          v-if="processing === 'read'"
          icon="spinner"
          spin
        />
        <font-awesome-icon
          v-else
          icon="envelope-open"
        />
        Mark as Read
      </button>

      <button
        v-if="!notification.is_archived"
        @click="archiveNotification"
        :disabled="processing"
        class="btn-action btn-archive"
      >
        <font-awesome-icon
          v-if="processing === 'archive'"
          icon="spinner"
          spin
        />
        <font-awesome-icon
          v-else
          icon="archive"
        />
        Archive
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useMainStore } from '@/stores/main'
import { useToast } from 'vue-toastification'
import User from '@/api/User'
import moment from 'moment'
import EditorJsRenderer from '@/components/EditorJs/EditorJsRenderer.vue'

const props = defineProps({
  notification: {
    type: Object,
    required: true
  }
})

const mainStore = useMainStore()
const toast = useToast()
const processing = ref(null)

async function markAsRead() {
  processing.value = 'read'
  try {
    const response = await User.markNotificationRead(props.notification.id)
    if (response.status === 'success') {
      props.notification.is_read = true
      toast.success('Marked as read')

      // Emit event to refresh parent list
      mainStore.event_emit('notification-updated')

      // Update unread count
      const countResponse = await User.getUnreadNotificationCount()
      if (countResponse.status === 'success') {
        mainStore.update_notification_count(countResponse.count)
      }
    } else {
      toast.error('Failed to mark as read')
    }
  } catch (error) {
    console.error('Error marking as read:', error)
    toast.error('Failed to mark as read')
  } finally {
    processing.value = null
  }
}

async function archiveNotification() {
  processing.value = 'archive'
  try {
    const response = await User.archiveNotification(props.notification.id)
    if (response.status === 'success') {
      props.notification.is_archived = true
      toast.success('Notification archived')

      // Emit event to refresh parent list
      mainStore.event_emit('notification-updated')

      // Close slideover after short delay
      setTimeout(() => {
        mainStore.close_slideover()
      }, 500)
    } else {
      toast.error('Failed to archive notification')
      processing.value = null
    }
  } catch (error) {
    console.error('Error archiving notification:', error)
    toast.error('Failed to archive notification')
    processing.value = null
  }
}

function formatTimestamp(timestamp) {
  return moment(timestamp).fromNow()
}

function formatFullTimestamp(timestamp) {
  return moment(timestamp).format('MMM D, YYYY [at] h:mm A')
}

function getSeverityLabel(severity) {
  const labels = {
    0: 'Info',
    1: 'Low',
    2: 'Medium',
    3: 'High',
    4: 'Critical'
  }
  return labels[severity] || 'Unknown'
}
</script>

<style scoped>
.notification-detail {
  height: 100%;
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  color: white;
}

.detail-container {
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
  min-height: 0;
}

/* Header */
.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.severity-badge {
  padding: 0.375rem 1rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.severity-0 {
  background: rgba(59, 130, 246, 0.2);
  color: #60a5fa;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

.severity-1 {
  background: rgba(34, 197, 94, 0.2);
  color: #4ade80;
  border: 1px solid rgba(34, 197, 94, 0.3);
}

.severity-2 {
  background: rgba(245, 158, 11, 0.2);
  color: #fbbf24;
  border: 1px solid rgba(245, 158, 11, 0.3);
}

.severity-3 {
  background: rgba(249, 115, 22, 0.2);
  color: #fb923c;
  border: 1px solid rgba(249, 115, 22, 0.3);
}

.severity-4 {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.timestamp {
  font-size: 0.875rem;
  color: #9ca3af;
}

/* Title */
.notification-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #f3f4f6;
  margin: 0 0 1.5rem 0;
  line-height: 1.3;
}

/* EditorJS Content */
.notification-content {
  margin-bottom: 2rem;
}

/* Action Footer */
.action-footer {
  padding: 1.5rem 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background-color: #191f27;
  display: flex;
  gap: 0.75rem;
  flex-shrink: 0;
}

.btn-action {
  flex: 1;
  padding: 0.875rem 1.5rem;
  background-color: #1a1d24;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 0.5rem;
  color: #9ca3af;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-action:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-action:hover:not(:disabled) {
  background-color: #252930;
  border-color: rgba(255, 255, 255, 0.3);
  color: #f3f4f6;
}

/* Responsive Design */
@media (max-width: 768px) {
  .detail-container {
    padding: 1.5rem;
  }

  .notification-title {
    font-size: 1.25rem;
  }

  .notification-message {
    font-size: 0.9375rem;
  }

  .action-footer {
    padding: 1rem 1.5rem;
    flex-direction: column;
  }

  .btn-action {
    width: 100%;
  }
}
</style>
